import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const API_BASE = `${API_URL}/api`;

const AuthContext = createContext(null);

// Axios instance — credentials (httpOnly cookies) sent automatically
const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// Refresh lock to avoid parallel refresh attempts
let refreshing = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      error.response?.data?.code === 'TOKEN_EXPIRED' &&
      !original._retry
    ) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshing = null; });
        }
        await refreshing;
        return api(original);
      } catch (_) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount — cookie sent automatically
  useEffect(() => {
    api.get('/auth/me')
      .then(({ data }) => setUser(data.user))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Handle OAuth callback — Google sets cookies server-side, just fetch /me
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_error')) {
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    // Clean up any legacy token params in URL
    if (params.get('token')) {
      window.history.replaceState({}, '', window.location.pathname);
      api.get('/auth/me')
        .then(({ data }) => setUser(data.user))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, []);

  const register = useCallback(async (name, email, password, data_consent) => {
    const { data } = await api.post('/auth/register', { name, email, password, data_consent });
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (_) {}
    setUser(null);
  }, []);

  const loginWithGoogle = useCallback(() => {
    window.location.href = `${API_BASE}/auth/google`;
  }, []);

  const isPremium = user?.subscription_tier === 'premium' || user?.is_tester;

  function getEffectiveTier(u) {
    if (!u) return 'basic';
    const tier = u.tier || 'free';
    if (tier === 'free') {
      const exp = u.tier_expires_at ? new Date(u.tier_expires_at) : null;
      if (exp && new Date() < exp) return 'premium';
      return 'basic';
    }
    return tier;
  }

  const effectiveTier = getEffectiveTier(user);
  const isEffectivePremium = effectiveTier === 'premium';

  const acceptTerms = useCallback(async (version) => {
    const { data } = await api.post('/auth/accept-terms', { version });
    setUser((prev) => prev ? { ...prev, tc_accepted_at: data.user.tc_accepted_at } : prev);
    return data.user;
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isPremium,
      effectiveTier,
      isEffectivePremium,
      register,
      login,
      logout,
      loginWithGoogle,
      acceptTerms,
      setUser,
      api,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { api };
